import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AccessibilityIcon, Check, Copy, EyeOff, Info, Pencil } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge } from 'slab'
import { formatSSN, getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdCoveredParticipants } from 'utils/web_api'
import { type BrokersCoveredParticipant } from 'utils/web_api.schemas'

export const COVERED_PARTICIPANTS_TABLE_FEATURES = {
  EDIT_PARTICIPANT: 'covered_participants_edit_participant',
} as const

export type CoveredParticipantsViewViewFeature = typeof COVERED_PARTICIPANTS_TABLE_FEATURES[
  keyof typeof COVERED_PARTICIPANTS_TABLE_FEATURES
]

interface CoveredParticipantsTableProps {
  memberId: string
  planYear?: number
  onEdit: (participant: BrokersCoveredParticipant) => void
}

const columnHelper = createColumnHelper<BrokersCoveredParticipant>()

const CopyButton = ({ content }: { content: string }) => (
  <CopyButtonWithAnimation content={content}>
    <Copy className='h-4 w-4 text-gray-500' aria-label='Copy' />
  </CopyButtonWithAnimation>
)

const CopyableCell = ({ value }: { value?: string | number }) => {
  if (!value) {
    return null
  }

  const stringValue = typeof value === 'string' ? value : value.toString()

  return (
    <div className='flex items-center gap-2'>
      <span>{value}</span>
      <CopyButton content={stringValue} />
    </div>
  )
}

const createColumns = (onEdit: (participant: BrokersCoveredParticipant) => void) => [
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <button
        onClick={() => onEdit(row.original)}
        className='p-2 hover:bg-gray-100 rounded-full transition-colors'
        data-testid='edit-participant-button'
        aria-label='Edit participant'
      >
        <Pencil className='h-4 w-4 text-gray-500' />
      </button>
    ),
  }),
  columnHelper.accessor('given_name', {
    header: 'Given name',
    cell: ({ getValue }) => <CopyableCell value={getValue()} />,
  }),
  columnHelper.accessor('family_name', {
    header: 'Family name',
    cell: ({ getValue }) => <CopyableCell value={getValue()} />,
  }),
  columnHelper.display({
    id: 'source',
    header: 'Source',
    cell: ({ row }) => {
      const qleId = row.original.qualifying_life_event_id
      const qleStatus = row.original.qle_status
      if (qleId) {
        return (
          <DisplayTooltipOnHover
            text={
              qleStatus === 'in_progress'
                ? `QLE in progress: ${qleId.slice(-6)}`
                : `QLE added participant: ${qleId.slice(-6)}`
            }
          >
            <Badge
              type={qleStatus === 'in_progress' ? 'warning' : 'success'}
              leftIcon={
                qleStatus === 'in_progress' ? ExclamationTriangleIcon : Check
              }
            >
              QLE
            </Badge>
          </DisplayTooltipOnHover>
        )
      } else {
        return null
      }
    },
  }),
  columnHelper.accessor('relationship', {
    header: 'Relationship',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('gender', {
    header: 'Gender',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('dob', {
    header: 'Date of Birth',
    cell: ({ getValue }) => {
      const value = getValue()
      if (!value) return null

      const date = new Date(value)
      const formattedDate = getLocaleDateString(date)

      return <CopyableCell value={formattedDate} />
    },
  }),
  columnHelper.accessor('age', {
    header: 'Age',
    cell: ({ getValue, row }) => {
      const hasDisability = row.original.has_disability
      return (
        <div className='flex gap-2'>
          <CopyableCell value={getValue()} />
          {hasDisability && (
            <DisplayTooltipOnHover text='This dependent has a disability and is eligible for coverage past the age of 26.'>
              <AccessibilityIcon className='h-4 w-4 text-gray-500' />
            </DisplayTooltipOnHover>
          )}
        </div>
      )
    }
  }),
  columnHelper.accessor('ssn', {
    header: 'SSN',
    cell: ({ getValue }) => {
      const value = getValue()
      if (!value) {
        return null
      }

      const [isVisible, setIsVisible] = useState(false)
      return (
        <div className='flex items-center gap-2'>
          {isVisible
            ? (
              <>
                <span>{formatSSN(value)}</span>
                <button
                  onClick={() => setIsVisible(false)}
                  className='p-1 hover:bg-gray-100 rounded-full transition-colors'
                  aria-label='Hide SSN'
                >
                  <EyeOff className='h-3 w-3 text-gray-500' />
                </button>
                <CopyButton content={value} />
              </>
              )
            : (
              <>
                <button
                  data-reveal-target='item'
                  onClick={() => setIsVisible(true)}
                  aria-label='Show SSN'
                  className='button-outline text-xs h-4'
                >
                  Show
                </button>
                <CopyButton content={value} />
              </>
              )}
        </div>
      )
    },
  }),
  columnHelper.accessor('tobacco', {
    header: 'Tobacco use',
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor('status', {
    header: () => (
      <div className='flex gap-1'>
        <span>Status</span>
        <DisplayTooltipOnHover text="'Active' means a dependent is enrolled in coverage. 'Pending' means the dependent has not yet enrolled.">
          <Info className='w-4' />
        </DisplayTooltipOnHover>
      </div>
    ),
    cell: ({ getValue }) => (getValue() === 'pending' ? 'Pending' : 'Active'),
  }),
]

const CoveredParticipantsTable = ({
  memberId,
  planYear,
  onEdit,
}: CoveredParticipantsTableProps) => {
  const { enabledFeatures } = useContext(BrokersMemberContext)

  // Memoize query parameters to prevent infinite re-renders
  const queryParams = useMemo(() => ({
    plan_year: planYear || 0,
    include_pending: true,
  }), [planYear])

  const { data, isLoading, error } =
    useGetBrokersMembersMemberIdCoveredParticipants(
      memberId,
      queryParams,
      {
        query: {
          enabled: !!memberId,
          refetchOnWindowFocus: false,
        },
      }
    )

  // Memoize participants data to prevent infinite re-renders
  const participants = useMemo(() => {
    return data || []
  }, [data])
  const showSsnColumn = useMemo(() => {
    // We determine whether to show the SSN column this way because it aligns
    // with how we implement data access control on the backend. That is, the
    // Grape entity doesn't return this data if the user's not allowed to
    // access it. So, if they're not allowed to access it, we don't show it.
    // That said, in the future, we may reconsider using the enabled-features
    // list to control display/hide here.
    return participants.some(p => p.ssn !== undefined)
  }, [participants])

  const columns = useMemo(() => createColumns(onEdit), [onEdit])

  const enableEditParticipants = Boolean(enabledFeatures?.includes(
    COVERED_PARTICIPANTS_TABLE_FEATURES.EDIT_PARTICIPANT
  ))
  const table = useReactTable({
    data: participants,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        actions: enableEditParticipants,
        ssn: showSsnColumn,
      },
    },
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No covered participants found'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='participant'
      paginationLabelPlural='participants'
      loadingSkeletonPageSizeOverride={2}
    />
  )
}

export default CoveredParticipantsTable
